import React, { useEffect, useState } from "react";
import { Plus, Gift, Megaphone, Check, X, Trash2, Loader2, Eye, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(); } catch { return s; } };

// ============================================================
// In-Kind Donations Panel
// ============================================================
export const InKindPanel = () => {
    const [data, setData] = useState({ items: [], total_fmv: 0, count: 0 });
    const [scheduleM, setScheduleM] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        donor_name: "",
        donor_email: "",
        item_description: "",
        category: "goods",
        fair_market_value: "",
        date_received: new Date().toISOString().slice(0, 10),
        notes: "",
    });
    const [creating, setCreating] = useState(false);

    const load = () =>
        Promise.all([
            api.get("/admin/in-kind").then((r) => setData(r.data)).catch(() => {}),
            api.get("/admin/reports/schedule-m").then((r) => setScheduleM(r.data)).catch(() => setScheduleM(null)),
        ]).finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            await api.post("/admin/in-kind", {
                ...form,
                fair_market_value: parseFloat(form.fair_market_value),
            });
            toast.success("In-kind donation recorded");
            setForm({ ...form, donor_name: "", donor_email: "", item_description: "", fair_market_value: "", notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setCreating(false);
        }
    };

    const remove = async (iid) => {
        if (!window.confirm("Delete this in-kind record?")) return;
        await api.delete(`/admin/in-kind/${iid}`);
        load();
    };

    return (
        <div data-testid="admin-in-kind-panel">
            <div className="bg-white border border-black/5 rounded-3xl p-8 mb-8">
                <div className="flex items-start gap-3 mb-6">
                    <Gift className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium">In-kind donations</h2>
                        <p className="text-sm text-[#4A5568] mt-1.5 max-w-2xl">
                            Record non-cash gifts (gift cards, supplies, professional services). Each entry captures the donor,
                            item description, fair market value, and category. Totals here feed into your books for Form 990 reporting.
                        </p>
                    </div>
                </div>

                {!loading && (
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                        <div className="bg-[#FDFBF7] rounded-xl px-5 py-4">
                            <p className="text-xs uppercase tracking-wider text-[#4A5568] font-bold">Total FMV recorded</p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium mt-1" data-testid="admin-in-kind-total">{fmtMoney(data.total_fmv)}</p>
                        </div>
                        <div className="bg-[#FDFBF7] rounded-xl px-5 py-4">
                            <p className="text-xs uppercase tracking-wider text-[#4A5568] font-bold">Entries</p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium mt-1" data-testid="admin-in-kind-count">{data.count}</p>
                        </div>
                    </div>
                )}

                {scheduleM && (
                    <div className={`rounded-2xl p-5 mb-6 border ${scheduleM.schedule_m_required ? "bg-amber-50/70 border-amber-200" : "bg-green-50/70 border-green-200"}`} data-testid="admin-schedule-m-widget">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5568]">Form 990 · Schedule M · {scheduleM.tax_year}</p>
                                <p className="font-heading text-2xl text-[#1C252E] font-medium mt-1">
                                    {fmtMoney(scheduleM.total_fmv)} <span className="text-sm text-[#4A5568] font-normal">across {scheduleM.count} gift{scheduleM.count === 1 ? "" : "s"}</span>
                                </p>
                                <p className={`text-xs font-semibold mt-1 ${scheduleM.schedule_m_required ? "text-amber-800" : "text-green-800"}`} data-testid="admin-schedule-m-status">
                                    {scheduleM.schedule_m_required
                                        ? `⚠ Schedule M filing required this year${scheduleM.single_items_over_5k.length ? ` (${scheduleM.single_items_over_5k.length} item${scheduleM.single_items_over_5k.length === 1 ? "" : "s"} > $5k)` : ""}`
                                        : "✓ Below both thresholds — no Schedule M filing required this year"}
                                </p>
                                <p className="text-[10px] text-[#4A5568] mt-2 max-w-xl">{scheduleM.threshold_note}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5568]">By category</p>
                                <div className="text-xs text-[#1C252E] mt-1 space-y-0.5">
                                    {Object.keys(scheduleM.by_category).length === 0 && <p className="text-[#9CA3AF] italic">No gifts yet</p>}
                                    {Object.entries(scheduleM.by_category).map(([cat, v]) => (
                                        <p key={cat}><span className="capitalize">{cat.replace("_", " ")}</span>: <strong>{fmtMoney(v.total_fmv)}</strong> ({v.count})</p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={create} className="bg-[#FDFBF7] rounded-2xl p-5 mb-6" data-testid="admin-in-kind-create-form">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-4">Record a new in-kind gift</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <input required placeholder="Donor name *" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} data-testid="admin-in-kind-donor-name-input" className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm" />
                        <input type="email" placeholder="Donor email (optional)" value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} data-testid="admin-in-kind-donor-email-input" className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm" />
                    </div>
                    <input required placeholder="Item description (e.g. '40 Target gift cards $25 ea')" maxLength={300} value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} data-testid="admin-in-kind-item-input" className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm mb-3" />
                    <div className="grid sm:grid-cols-3 gap-3 mb-3">
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-in-kind-category-select" className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm">
                            <option value="goods">Goods → 1100 Inventory</option>
                            <option value="raffle_prize">Raffle prize → 1150 Donated Prize Assets</option>
                            <option value="gift_cards">Gift cards → 1200 Prepaid</option>
                            <option value="services">Services → 5020 Program Expense</option>
                            <option value="professional_services">Professional services → 5040 Mgmt & General</option>
                        </select>
                        <input required type="number" step="0.01" min="0.01" placeholder="Fair market value *" value={form.fair_market_value} onChange={(e) => setForm({ ...form, fair_market_value: e.target.value })} data-testid="admin-in-kind-fmv-input" className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm" />
                        <input required type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} data-testid="admin-in-kind-date-input" className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm" />
                    </div>
                    <textarea placeholder="Notes (optional)" rows={2} maxLength={600} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="admin-in-kind-notes-input" className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm resize-none mb-3" />
                    <button type="submit" disabled={creating} data-testid="admin-in-kind-create-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Record gift
                    </button>
                </form>

                {loading ? <p className="text-[#4A5568] text-sm">Loading…</p> : data.items.length === 0 ? (
                    <p className="text-[#4A5568] text-sm" data-testid="admin-in-kind-empty">No in-kind donations recorded yet.</p>
                ) : (
                    <div className="overflow-x-auto" data-testid="admin-in-kind-list">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-[#4A5568]">
                                    <th className="py-3 pr-4">Date</th>
                                    <th className="py-3 pr-4">Donor</th>
                                    <th className="py-3 pr-4">Item</th>
                                    <th className="py-3 pr-4">Category</th>
                                    <th className="py-3 pr-4 text-right">FMV</th>
                                    <th className="py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((i) => (
                                    <tr key={i.id} className="border-b border-black/5" data-testid={`admin-in-kind-row-${i.id}`}>
                                        <td className="py-3 pr-4 text-[#4A5568] text-xs">{fmtDate(i.date_received)}</td>
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-[#1C252E]">{i.donor_name}</p>
                                            {i.donor_email && <p className="text-xs text-[#4A5568]">{i.donor_email}</p>}
                                        </td>
                                        <td className="py-3 pr-4 text-[#1C252E]">
                                            {i.item_description}
                                            {i.journal_entry_id && (
                                                <p className="text-[10px] text-green-700 mt-1" title={`Journal entry ${i.journal_entry_id}`}>
                                                    ✓ Booked to {i.posted_debit_account || "—"} / 4025
                                                </p>
                                            )}
                                            {!i.journal_entry_id && (
                                                <p className="text-[10px] text-amber-700 mt-1">
                                                    ⚠ Not yet posted to books
                                                </p>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4 text-[#4A5568] text-xs capitalize">{i.category.replace("_", " ")}</td>
                                        <td className="py-3 pr-4 text-right font-medium text-[#1C252E]">{fmtMoney(i.fair_market_value)}</td>
                                        <td className="py-3">
                                            <button onClick={() => remove(i.id)} data-testid={`admin-in-kind-delete-${i.id}`} className="text-[#E53935] hover:text-[#D32F2F] p-1.5" title="Delete + void linked JE">
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};


// ============================================================
// Organizer Requests / Fundraisers Panel
// ============================================================
export const OrganizerRequestsPanel = () => {
    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/organizer-requests", { params: filter ? { status: filter } : {} })
            .then((r) => setUsers(r.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(load, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

    const decide = async (uid, status) => {
        const note = status === "rejected" ? window.prompt("Optional note explaining why (sent to user):", "") : "";
        if (note === null) return; // user cancelled
        setBusyId(uid);
        try {
            await api.patch(`/admin/users/${uid}/organizer-status`, { status, admin_note: note || "" });
            toast.success(`User ${status}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="admin-organizer-panel">
            <div className="bg-white border border-black/5 rounded-3xl p-8">
                <div className="flex items-start gap-3 mb-6">
                    <Megaphone className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium">P2P organizer requests</h2>
                        <p className="text-sm text-[#4A5568] mt-1.5 max-w-2xl">
                            Approve users to create peer-to-peer fundraising pages. Teachers are auto-flagged for fast review.
                        </p>
                    </div>
                </div>

                <div className="flex gap-2 mb-6" role="group" aria-label="Filter by status">
                    {[
                        { v: "pending", l: "Pending" },
                        { v: "approved", l: "Approved" },
                        { v: "rejected", l: "Rejected" },
                        { v: "", l: "All" },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setFilter(f.v)}
                            data-testid={`admin-organizer-filter-${f.v || "all"}-btn`}
                            aria-pressed={filter === f.v}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium ${filter === f.v ? "bg-[#1C252E] text-white" : "bg-[#F4F1EA] text-[#4A5568] hover:bg-[#F2D8D5]"}`}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>

                {loading ? <p className="text-[#4A5568] text-sm">Loading…</p> : users.length === 0 ? (
                    <p className="text-[#4A5568] text-sm" data-testid="admin-organizer-empty">No {filter} organizer requests.</p>
                ) : (
                    <div className="space-y-3" data-testid="admin-organizer-list">
                        {users.map((u) => (
                            <div key={u.user_id} className="bg-[#FDFBF7] rounded-2xl p-5" data-testid={`admin-organizer-row-${u.user_id}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-medium text-[#1C252E]">{u.name || u.email}</p>
                                            <span className="px-2 py-0.5 rounded-full bg-white border border-black/10 text-xs text-[#4A5568] capitalize">
                                                {u.organizer_role_claim}
                                            </span>
                                            {u.organizer_fast_flag && (
                                                <span className="px-2 py-0.5 rounded-full bg-[#E53935]/10 text-[#E53935] text-xs font-medium">
                                                    ⭐ Fast review
                                                </span>
                                            )}
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                u.organizer_status === "approved" ? "bg-green-100 text-green-700" :
                                                u.organizer_status === "rejected" ? "bg-red-100 text-red-700" :
                                                "bg-amber-100 text-amber-700"
                                            }`}>{u.organizer_status}</span>
                                        </div>
                                        <p className="text-xs text-[#4A5568] mt-1">{u.email}</p>
                                        {(u.organizer_school || u.organizer_grade) && (
                                            <p className="text-xs text-[#4A5568] mt-1">{u.organizer_school} {u.organizer_grade && `· ${u.organizer_grade}`}</p>
                                        )}
                                        {u.organizer_note_to_admin && (
                                            <p className="text-sm text-[#1C252E] mt-2 bg-white rounded p-2 border border-black/5">
                                                <em>"{u.organizer_note_to_admin}"</em>
                                            </p>
                                        )}
                                    </div>
                                    {u.organizer_status === "pending" && (
                                        <div className="flex gap-2 flex-shrink-0">
                                            <button onClick={() => decide(u.user_id, "approved")} disabled={busyId === u.user_id} data-testid={`admin-organizer-approve-${u.user_id}`} className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-full px-4 py-1.5 text-xs font-medium">
                                                <Check className="w-3.5 h-3.5" /> Approve
                                            </button>
                                            <button onClick={() => decide(u.user_id, "rejected")} disabled={busyId === u.user_id} data-testid={`admin-organizer-reject-${u.user_id}`} className="inline-flex items-center gap-1 bg-white border border-[#E53935] text-[#E53935] hover:bg-[#F2D8D5] disabled:opacity-60 rounded-full px-4 py-1.5 text-xs font-medium">
                                                <X className="w-3.5 h-3.5" /> Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// ============================================================
// Combined wrapper for the new tab
// ============================================================
const Phase4_5Panel = () => (
    <div data-testid="admin-phase4-5-panel">
        <InKindPanel />
        <OrganizerRequestsPanel />
    </div>
);

export default Phase4_5Panel;
